import { useMemo } from 'react';
import { ThreadStatusResult } from '@weave/schema-gen-ts/dist/schemas/sms/data/v4/sms_service.pb';
import { ThreadStatus } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { SMSDataV4 } from '@frontend/api-sms-data';
import { InboxLocationPhones, InboxLocationPhonesTypes } from '@frontend/inbox-location-phones';
import { PhoneOptionsProps } from './phone-options';

type FormattedStatusDataByLocation = {
  activeConversationsByLocation: Record<string, ThreadStatusResult[]>;
  activeConversationsCountByLocation: number;
  allConversationsByLocation: Record<string, ThreadStatusResult[]>;
  allConversationsCountByLocation: number;
};

export const useBatchGetThreadStatusQuery = (personPhone?: string) => {
  const locationPhonesQuery = InboxLocationPhones.Hooks.useLocationPhones();

  const { locationPhonesByLocation, filteredLocationPhonesByLocation } = useMemo<{
    locationPhonesByLocation: Record<string, string[]>;
    filteredLocationPhonesByLocation: Record<string, InboxLocationPhonesTypes.LocationPhone[]>;
  }>(() => {
    const newFilteredLocationPhonesByLocation: Record<string, InboxLocationPhonesTypes.LocationPhone[]> = {};
    const newLocationPhonesByLocation = Object.fromEntries(
      Object.entries(locationPhonesQuery.data.locationPhonesByLocation ?? {}).map(([locationId, entry]) => {
        const existingFilteredEntries = new Set(newFilteredLocationPhonesByLocation[locationId] || []);
        const result = [
          locationId,
          entry.map((e) => {
            existingFilteredEntries.add(e);
            return e.locationPhone;
          }),
        ];
        newFilteredLocationPhonesByLocation[locationId] = Array.from(existingFilteredEntries);
        return result;
      })
    );
    return {
      locationPhonesByLocation: newLocationPhonesByLocation,
      filteredLocationPhonesByLocation: newFilteredLocationPhonesByLocation,
    };
  }, [locationPhonesQuery.data.locationPhonesByLocation]);
  const requests = SMSDataV4.Utils.calculatePossibleThreadStatusRequests({
    personPhones: personPhone ? [personPhone] : [],
    locationPhonesByLocation,
  });

  const threadStatusQuery = SMSDataV4.Queries.useBatchGetThreadStatusQuery(
    {
      requests,
    },
    {
      enabled: !!personPhone,
      select: (data) => {
        return data.threadStatuses.reduce<FormattedStatusDataByLocation>(
          (acc, curr) => {
            // all conversations logic
            const allEntries = acc.allConversationsByLocation[curr.locationId] || [];
            acc.allConversationsByLocation[curr.locationId] = [...allEntries, curr];
            acc.allConversationsCountByLocation += 1;

            // active conversations logic
            if (curr.status === ThreadStatus.ACTIVE) {
              const existingEntries = acc.activeConversationsByLocation[curr.locationId] || [];
              acc.activeConversationsByLocation[curr.locationId] = [...existingEntries, curr];
              acc.activeConversationsCountByLocation += 1;
            }

            return acc;
          },
          {
            activeConversationsCountByLocation: 0,
            allConversationsCountByLocation: 0,
            activeConversationsByLocation: {},
            allConversationsByLocation: {},
          }
        );
      },
    }
  );

  const activeConversationCount = threadStatusQuery.data?.activeConversationsCountByLocation ?? 0;
  const hasActiveConversation = activeConversationCount > 0;

  const activeConversationsByLocation = useMemo(
    () => threadStatusQuery.data?.activeConversationsByLocation ?? {},
    [threadStatusQuery]
  );

  const allConversationsByLocation = useMemo(
    () => threadStatusQuery.data?.allConversationsByLocation ?? {},
    [threadStatusQuery]
  );

  const availableConversationsNumbersByLocation = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(allConversationsByLocation).map(([locationId, threadStatuses]) => [
          locationId,
          // filter out NEW or UNSPECIFIED statuses unless they have an enabled outbound number
          threadStatuses.filter(
            (ts) =>
              ts.status === ThreadStatus.ACTIVE ||
              ts.status === ThreadStatus.ARCHIVED ||
              locationPhonesQuery.enabledData.allLocationPhones.find(
                (locationData) => locationData.locationPhone === ts.locationPhone
              )
          ),
        ])
      ),
    [allConversationsByLocation, locationPhonesQuery.enabledData.allLocationPhones]
  );

  const allPhoneOptionsByLocation = useMemo(
    (): Record<string, PhoneOptionsProps['phones']> =>
      Object.fromEntries(
        Object.entries(allConversationsByLocation).map(([locationId, threadStatuses]) => [
          locationId,
          threadStatuses.map((ts) => {
            const matchingLocationPhone = locationPhonesQuery.data.locationPhonesByLocation?.[locationId]?.find(
              (lp) => lp.locationPhone === ts.locationPhone
            );
            return {
              ...ts,
              displayName: matchingLocationPhone?.displayName || undefined,
              defaultSms: matchingLocationPhone?.defaultSms,
            };
          }),
        ])
      ),
    [allConversationsByLocation, locationPhonesQuery.data.locationPhonesByLocation]
  );

  return {
    activeConversationCount,
    activeConversationsByLocation,
    allPhoneOptionsByLocation,
    availableConversationsNumbersByLocation,
    filteredLocationPhonesByLocation,
    hasActiveConversation,
    locationPhonesQuery,
  };
};
